/**
 * Meal picker.
 *
 * Horizontally scrolling cards on phones, matching the approved artwork, with
 * snap-to-card paging and a page indicator. Prices come from the API — the
 * component never knows what a meal costs until it is handed one.
 */

import { memo, useCallback, useRef, useState } from 'react';
import {
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';

import { formatMoney, type Meal } from '@mfa/domain';
import { mealImageSource } from '../../assets';
import { colors, radius, shadow, sizing, space } from '../../theme';
import { CheckIcon } from '../common/Icon';
import { Skeleton } from '../feedback/Skeleton';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';
import { useLocalized } from '../../core/i18n/content';

const CARD_WIDTH = sizing.mealCardWidth;
const GAP = space.md;

export interface MealCarouselProps {
  meals: Meal[];
  selectedId: string | null;
  onSelect: (meal: Meal) => void;
  loading?: boolean;
  testID?: string;
}

export const MealCarousel = memo(function MealCarousel({
  meals,
  selectedId,
  onSelect,
  loading = false,
  testID = 'meal-carousel',
}: MealCarouselProps) {
  const t = useT();
  const pick = useLocalized();
  const [page, setPage] = useState(0);
  const scrollRef = useRef<ScrollView>(null);

  const onScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offset = event.nativeEvent.contentOffset.x;
    setPage(Math.round(offset / (CARD_WIDTH + GAP)));
  }, []);

  if (loading) {
    return (
      <View style={styles.row} testID={`${testID}-loading`}>
        {[0, 1].map((i) => (
          <View key={i} style={[styles.card, styles.skeletonCard]}>
            <Skeleton height={sizing.mealCardImageHeight} />
            <Skeleton width="70%" height={18} style={styles.skeletonLine} />
            <Skeleton width="90%" height={14} style={styles.skeletonLine} />
            <Skeleton width="40%" height={20} style={styles.skeletonLine} />
          </View>
        ))}
      </View>
    );
  }

  return (
    <View testID={testID}>
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        snapToInterval={CARD_WIDTH + GAP}
        decelerationRate="fast"
        contentContainerStyle={styles.scrollContent}
        onScroll={onScroll}
        scrollEventThrottle={32}
      >
        {meals.map((meal) => {
          const selected = meal.id === selectedId;
          return (
            <Pressable
              key={meal.id}
              testID={`meal-card-${meal.id}`}
              onPress={() => onSelect(meal)}
              // Radio semantics: one meal at a time, announced as such.
              accessibilityRole="radio"
              accessibilityState={{ selected }}
              accessibilityLabel={`${pick(meal.name, meal.nameUr)}. ${formatMoney(meal.unitPrice)} per person. ${pick(meal.description, meal.descriptionUr)}`}
              style={[styles.card, selected && styles.cardSelected]}
            >
              <View style={styles.imageWrap}>
                {(() => {
                  const source = mealImageSource(meal.imageUrl, meal.name);
                  return source ? (
                    <Image
                      source={source}
                      style={styles.image}
                      accessibilityIgnoresInvertColors
                    />
                  ) : (
                    <View style={[styles.image, styles.imageFallback]} />
                  );
                })()}
                {selected ? (
                  <View style={styles.checkBadge}>
                    <CheckIcon size={24} color={colors.status.success.solid} />
                  </View>
                ) : null}
              </View>

              <View style={styles.body}>
                <Text variant="cardTitle" align="center" numberOfLines={2}>
                  {pick(meal.name, meal.nameUr)}
                </Text>
                <Text
                  variant="caption"
                  tone="secondary"
                  align="center"
                  numberOfLines={3}
                  style={styles.description}
                >
                  {pick(meal.description, meal.descriptionUr)}
                </Text>
                <Text variant="amount" tone="brand" align="center" style={styles.price}>
                  {formatMoney(meal.unitPrice)}
                </Text>
                <Text variant="caption" tone="tertiary" align="center">{t('Per Person')}</Text>
              </View>
            </Pressable>
          );
        })}
      </ScrollView>

      {meals.length > 1 ? (
        <View style={styles.dots} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
          {meals.map((meal, index) => (
            <View
              key={meal.id}
              style={[styles.dot, index === page && styles.dotActive]}
            />
          ))}
        </View>
      ) : null}
    </View>
  );
});

const styles = StyleSheet.create({
  scrollContent: {
    gap: GAP,
    paddingVertical: space.xs,
    paddingRight: space.lg,
  },
  row: {
    flexDirection: 'row',
    gap: GAP,
  },
  card: {
    width: CARD_WIDTH,
    backgroundColor: colors.bg.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    overflow: 'hidden',
    ...shadow('card'),
  },
  skeletonCard: {
    padding: space.md,
    gap: space.sm,
  },
  skeletonLine: {
    marginTop: space.sm,
  },
  cardSelected: {
    borderColor: colors.border.brand,
    borderWidth: 2,
  },
  imageWrap: {
    position: 'relative',
  },
  image: {
    width: '100%',
    height: sizing.mealCardImageHeight,
    resizeMode: 'cover',
  },
  imageFallback: {
    backgroundColor: colors.bg.surfaceSunken,
  },
  checkBadge: {
    position: 'absolute',
    top: space.sm,
    right: space.sm,
  },
  body: {
    padding: space.md,
    gap: space.xxs,
  },
  description: {
    minHeight: 36,
  },
  price: {
    marginTop: space.sm,
  },
  dots: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: space.xs,
    marginTop: space.md,
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: radius.full,
    backgroundColor: colors.border.strong,
  },
  dotActive: {
    backgroundColor: colors.text.brand,
    width: 18,
  },
});
